import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Default } from '../../components/default/Default';
import PostRequest from '../../services/PostRequest';
import { showToaster, handleCopy, GenerateKey } from '../../common/common';
import {
  postCodeDomainList,
  setZipCodeList,
  fetchOfficeworksStores,
} from '../../reducer/zipCode/ZipCode.action';
import { playgroundCreate } from '../../reducer/asin/Asin.action';
import './playground.css';

// Inline SVG icons
const AmazonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 16c4 3 14 3 18 0"/><path d="M19 14c1 3-1 5-1 5"/></svg>
);
const OfficeworksIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V8l8-5 8 5v13"/><path d="M9 21v-6h6v6"/></svg>
);
const RepcoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 11l1.4-4.2A2 2 0 0 1 8.3 5.4h7.4a2 2 0 0 1 1.9 1.4L19 11"/><path d="M3 11h18v5h-2v-1H5v1H3z"/><circle cx="7.5" cy="14.5" r="1"/><circle cx="16.5" cy="14.5" r="1"/></svg>
);
const InfoIcon = () => (
  <svg className="lead" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8h.01"/></svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
);
const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4 20-7Z"/></svg>
);
const CodeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="m8 8-4 4 4 4"/><path d="m16 8 4 4-4 4"/></svg>
);
const ResultsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h10M4 18h7"/></svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
);
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
);
const HelpIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="var(--mca-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 0 1 4 1.5c0 1.5-2 2-2 3"/><path d="M12 17h.01"/></svg>
);

const CodeSampleLanguages = ({ api, params, apiKey }) => {
  const [currentLang, setCurrentLang] = useState('url');

  // Build samples live from the current form values + the caller's real key.
  const key = apiKey || 'YOUR_API_KEY';
  const base = `https://api.multicartapi.com/v2/${api}`;
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  const qs = entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  const jsObj =
    '{ ' + entries.map(([k, v]) => `${k}: "${v}"`).join(', ') + ' }';
  const pyDict =
    '{' + entries.map(([k, v]) => `"${k}": "${v}"`).join(', ') + '}';

  const samples = {
    url: qs ? `${base}?${qs}` : base,
    curl:
      `curl "${base}" \\\n  -H "Authorization: Bearer ${key}" \\\n` +
      entries.map(([k, v]) => `  -d ${k}=${v}`).join(' \\\n'),
    node:
      `const res = await fetch("${base}", {\n` +
      `  method: "POST",\n` +
      `  headers: { Authorization: "Bearer ${key}" },\n` +
      `  body: JSON.stringify(${jsObj})\n` +
      `});\nconst data = await res.json();`,
    python:
      `import requests\n\n` +
      `res = requests.post(\n` +
      `    "${base}",\n` +
      `    headers={"Authorization": "Bearer ${key}"},\n` +
      `    data=${pyDict},\n` +
      `)\ndata = res.json()`,
    php:
      `$ch = curl_init("${base}");\n` +
      `curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer ${key}"]);\n` +
      `curl_setopt($ch, CURLOPT_POSTFIELDS, [\n` +
      entries.map(([k, v]) => `  "${k}" => "${v}",`).join('\n') +
      `\n]);\n$data = json_decode(curl_exec($ch));`,
  };

  const langLabel = { url: 'URL', curl: 'cURL', node: 'Node.js', python: 'Python', php: 'PHP' };
  const code = samples[currentLang];

  return (
    <div>
      <div className="lang-tabs">
        {['url', 'curl', 'node', 'python', 'php'].map((lang) => (
          <button
            key={lang}
            className="lang-tab"
            aria-selected={currentLang === lang}
            onClick={() => setCurrentLang(lang)}
          >
            {langLabel[lang]}
          </button>
        ))}
      </div>
      <div className="mca-code">
        <div className="mca-code__head">
          <span className="mca-code__lang">{langLabel[currentLang]}</span>
          <button
            className="mca-code__copy"
            onClick={() => handleCopy(code)}
            title="Copy code"
          >
            <CopyIcon />
            <span data-copy-label>Copy</span>
          </button>
        </div>
        <pre id="codeBody">{code}</pre>
      </div>
    </div>
  );
};

const ResultsPane = ({ data, loading }) => {
  if (loading) {
    return (
      <div id="resLoading">
        <div className="skel-line mca-skel" style={{ width: '40%', height: '14px' }} />
        <div className="skel-line mca-skel" style={{ width: '90%' }} />
        <div className="skel-line mca-skel" style={{ width: '80%' }} />
        <div className="skel-line mca-skel" style={{ width: '85%' }} />
        <div className="skel-line mca-skel" style={{ width: '60%' }} />
        <div className="skel-line mca-skel" style={{ width: '75%' }} />
        <div className="skel-line mca-skel" style={{ width: '50%' }} />
        <div style={{
          textAlign: 'center',
          color: 'var(--mca-muted)',
          fontSize: '13px',
          marginTop: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <span className="mca-spinner" style={{ color: 'var(--mca-primary)' }} />
          Running request…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div id="resEmpty" className="mca-empty">
        <div className="mca-empty__icon"><ResultsIcon /></div>
        <h3>No results yet</h3>
        <p>Send a request to preview the parsed JSON response here.</p>
      </div>
    );
  }

  return (
    <div id="resDone">
      <div className="req-meta">
        <CheckIcon />
        <span>Completed in <b>1.23s</b></span>
        <span style={{ color: 'var(--mca-muted)' }}>·</span>
        <span>Response size <b>4.2 KB</b></span>
        <span style={{ color: 'var(--mca-muted)' }}>·</span>
        <span className="mca-badge mca-badge--soft-success" style={{ marginLeft: 'auto' }}>200 OK</span>
      </div>
      <div className="mca-code">
        <div className="mca-code__head">
          <span className="mca-code__lang">response.json</span>
          <button className="mca-code__copy" onClick={() => handleCopy(JSON.stringify(data, null, 2))}>
            <CopyIcon />
            <span data-copy-label>Copy</span>
          </button>
        </div>
        <pre id="jsonBody">{JSON.stringify(data, null, 2)}</pre>
      </div>
    </div>
  );
};

export const Playground = () => {
  const dispatch = useDispatch();
  const [showAlert, setShowAlert] = useState(true);
  const [activeApi, setActiveApi] = useState('amazon');
  const [activeResultPane, setActiveResultPane] = useState('code');
  const [loading, setLoading] = useState(false);
  const [apiKey, setApiKey] = useState('');

  // Form refs and state
  const amazonFormRef = useRef(null);
  const officeworksFormRef = useRef(null);

  const [amazonState, setAmazonState] = useState({
    domain: '',
    asin: 'B0CX23V2ZK',
    postcode: '3000',
    output: 'JSON',
    includeRawHtml: false,
  });

  const [officeworksState, setOfficeworksState] = useState({
    sku: 'AB12345',
    store: '0123',
    postcode: '3000',
  });

  const [repcoState, setRepcoState] = useState({
    sku: 'A1547402',
    postcode: '',
  });

  // Redux selectors
  const _domainList = useSelector((state) => state.zipCodeReducer.domainList || []);
  const _zipCodesList = useSelector((state) => state.zipCodeReducer.zipCodesList || []);
  const _officeworksStoreList = useSelector((state) => state.zipCodeReducer.officeworksStoreList || []);
  const _playgroundData = useSelector((state) => state.asinReducer.playgroundData);

  // Load domain and zip code lists on mount
  useEffect(() => {{
    dispatch(setZipCodeList({ status: 'active' }));
    dispatch(postCodeDomainList({ status: 'active' }));
  }}, [dispatch]);

  // Load the user's real API key so the code samples are copy-paste ready.
  useEffect(() => {
    (async () => {
      try {
        const res = await PostRequest('/users/account-details');
        if (res?.api_key) setApiKey(res.api_key);
      } catch (e) {
        /* fall back to YOUR_API_KEY in the samples */
      }
    })();
  }, []);

  // Load officeworks stores when API changes to officeworks
  useEffect(() => {
    if (activeApi === 'officeworks') {
      dispatch(fetchOfficeworksStores());
    }
  }, [activeApi, dispatch]);

  // Handle API selector change
  const handleApiChange = (api) => {
    setActiveApi(api);
    setActiveResultPane('code');
  };

  // Handle form submit
  const handleSend = async () => {
    try {
      setLoading(true);
      setActiveResultPane('results');

      // Map each supplier's form state to the exact field names its BE endpoint
      // requires (customer_postcode / store_id / include_raw_html).
      let formData, endpoint;
      if (activeApi === 'amazon') {
        endpoint = '/playground/post/';
        formData = {
          domain: amazonState.domain,
          asin: amazonState.asin,
          customer_postcode: amazonState.postcode,
          output: amazonState.output,
          include_raw_html: amazonState.includeRawHtml,
        };
      } else if (activeApi === 'repco') {
        // Repco is postcode-driven (no store); the BE resolves the id/string.
        endpoint = '/playground/repco/post/';
        formData = { sku: repcoState.sku, customer_postcode: repcoState.postcode };
      } else {
        endpoint = '/playground/officeworks/post/';
        formData = {
          sku: officeworksState.sku,
          store_id: officeworksState.store,
          customer_postcode: officeworksState.postcode,
        };
      }

      const response = await PostRequest(endpoint, formData);
      dispatch(playgroundCreate(response));
      showToaster('Request completed successfully', 'success');
    } catch (error) {
      console.error('API Error:', error);
      showToaster(error.message || 'Request failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Resolve the form's IDs to human-readable values for the code samples.
  const selectedDomainLabel =
    _domainList.find((d) => String(d.id) === String(amazonState.domain))?.domain ||
    'amazon.com.au';
  const amazonPostcodeLabel =
    _zipCodesList.find((z) => String(z.id) === String(amazonState.postcode))?.zipcode ||
    amazonState.postcode;
  const owPostcodeLabel =
    _zipCodesList.find((z) => String(z.id) === String(officeworksState.postcode))?.zipcode ||
    officeworksState.postcode;
  const repcoPostcodeLabel =
    _zipCodesList.find((z) => String(z.id) === String(repcoState.postcode))?.zipcode ||
    repcoState.postcode;

  // Postcodes belong to a domain — only offer the selected Amazon domain's
  // postcodes (e.g. AU postcodes for amazon.com.au), and only officeworks
  // postcodes in the Officeworks form.
  const amazonDomainName = _domainList.find(
    (d) => String(d.id) === String(amazonState.domain)
  )?.domain;
  const amazonZipCodes = amazonDomainName
    ? _zipCodesList.filter((z) => z?.domain?.domain === amazonDomainName)
    : _zipCodesList;
  const officeworksZipCodes = _zipCodesList.filter(
    (z) => z?.domain?.domain === 'officeworks.com.au'
  );
  const repcoZipCodes = _zipCodesList.filter(
    (z) => z?.domain?.domain === 'repco.com.au'
  );

  const sampleParams =
    activeApi === 'amazon'
      ? { domain: selectedDomainLabel, asin: amazonState.asin, postcode: amazonPostcodeLabel }
      : activeApi === 'repco'
      ? { sku: repcoState.sku, postcode: repcoPostcodeLabel }
      : { sku: officeworksState.sku, store: officeworksState.store, postcode: owPostcodeLabel };

  return (
    <Default>
      <div className="mca-content">
        {/* Page header */}
        <div className="mca-page-head">
          <h1>API Playground</h1>
          <p>Build and run a live request against the Amazon, Officeworks, or Repco API.</p>
        </div>

        {/* Welcome alert */}
        {showAlert && (
          <div className="mca-alert mca-alert--info" role="status" style={{ marginBottom: '20px' }}>
            <InfoIcon />
            <div className="mca-alert__body">
              <div className="mca-alert__title">Welcome to the API Playground</div>
              Fill in the form and hit Send to preview a real response. <a href="/documentation-list">Read the API docs →</a>
            </div>
            <button
              className="mca-overlay-close"
              aria-label="Dismiss"
              style={{ background: 'transparent' }}
              onClick={() => setShowAlert(false)}
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {/* Main grid: form | results */}
        <div className="pg-grid">
          {/* LEFT: Request form card */}
          <div className="mca-card">
            <div className="mca-card__head">
              <div className="mca-card__title">Request</div>
            </div>
            <div className="mca-card__body">
              {/* API Selector */}
              <div className="mca-seg" style={{ width: '100%', marginBottom: '22px' }}>
                <button
                  className="mca-seg__btn"
                  aria-selected={activeApi === 'amazon'}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => handleApiChange('amazon')}
                >
                  <AmazonIcon />
                  Amazon
                </button>
                <button
                  className="mca-seg__btn"
                  aria-selected={activeApi === 'officeworks'}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => handleApiChange('officeworks')}
                >
                  <OfficeworksIcon />
                  Officeworks
                </button>
                <button
                  className="mca-seg__btn"
                  aria-selected={activeApi === 'repco'}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={() => handleApiChange('repco')}
                >
                  <RepcoIcon />
                  Repco
                </button>
              </div>

              {/* Amazon form */}
              {activeApi === 'amazon' && (
                <form className="form-stack" ref={amazonFormRef}>
                  <div className="pg-section-label">Product</div>

                  {/* Domain */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="az-domain">
                      Domain <span className="req">*</span>
                      <span className="mca-tip">
                        <HelpIcon />
                        <span className="mca-tip__bubble">The Amazon marketplace to query.</span>
                      </span>
                    </label>
                    <select
                      className="mca-select"
                      id="az-domain"
                      value={amazonState.domain}
                      onChange={(e) =>
                        setAmazonState({ ...amazonState, domain: e.target.value, postcode: '' })
                      }
                    >
                      <option value="">Select domain</option>
                      {_domainList.map((domain) => (
                        <option key={domain.id} value={domain.id}>
                          {domain.domain}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ASIN */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="az-asin">
                      ASIN <span className="req">*</span>
                    </label>
                    <input
                      className="mca-input mca-cell-mono"
                      id="az-asin"
                      value={amazonState.asin}
                      onChange={(e) => setAmazonState({ ...amazonState, asin: e.target.value })}
                      maxLength="20"
                    />
                    <span className="mca-help">10-character Amazon product identifier.</span>
                  </div>

                  <div className="pg-section-label" style={{ marginTop: '8px' }}>Customer</div>

                  {/* Postcode */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="az-postcode">
                      Customer postcode <span className="req">*</span>
                    </label>
                    <select
                      className="mca-select"
                      id="az-postcode"
                      value={amazonState.postcode}
                      onChange={(e) => setAmazonState({ ...amazonState, postcode: e.target.value })}
                    >
                      <option value="">Select postcode...</option>
                      {amazonZipCodes.map((zc) => (
                        <option key={zc.id} value={zc.id}>
                          {zc.zipcode}
                        </option>
                      ))}
                    </select>
                    <span className="mca-help">
                      The postcode is applied for <b>amazon.com.au</b> and <b>amazon.com</b> only.
                      For all other marketplaces, results are returned at the site&apos;s default
                      location — customer-exact postcode delivery is available for those domains via
                      the full scraping product (Collections).
                    </span>
                  </div>

                  <div className="pg-section-label" style={{ marginTop: '8px' }}>Output</div>

                  {/* Output format */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="az-output">
                      Format
                    </label>
                    <select
                      className="mca-select"
                      id="az-output"
                      value={amazonState.output}
                      onChange={(e) => setAmazonState({ ...amazonState, output: e.target.value })}
                    >
                      <option>JSON</option>
                      <option>XML</option>
                    </select>
                  </div>

                  {/* Include raw HTML toggle */}
                  <label className="mca-switch" style={{ justifyContent: 'space-between' }}>
                    <span>
                      <span style={{
                        fontFamily: 'var(--mca-font-heading)',
                        fontWeight: '600',
                        fontSize: '14px',
                      }}>
                        Include raw HTML
                      </span>
                      <br />
                      <span className="mca-help">Returns the source page alongside parsed data.</span>
                    </span>
                    <span style={{ display: 'inline-flex' }}>
                      <input
                        type="checkbox"
                        checked={amazonState.includeRawHtml}
                        onChange={(e) => setAmazonState({ ...amazonState, includeRawHtml: e.target.checked })}
                      />
                      <span className="mca-switch__track" />
                    </span>
                  </label>
                </form>
              )}

              {/* Officeworks form */}
              {activeApi === 'officeworks' && (
                <form className="form-stack" ref={officeworksFormRef}>
                  <div className="pg-section-label">Product</div>

                  {/* SKU */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="ow-sku">
                      SKU <span className="req">*</span>
                    </label>
                    <input
                      className="mca-input mca-cell-mono"
                      id="ow-sku"
                      value={officeworksState.sku}
                      onChange={(e) => setOfficeworksState({ ...officeworksState, sku: e.target.value })}
                    />
                  </div>

                  {/* Store */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="ow-store">
                      Store <span className="req">*</span>
                    </label>
                    <select
                      className="mca-select"
                      id="ow-store"
                      value={officeworksState.store}
                      onChange={(e) => setOfficeworksState({ ...officeworksState, store: e.target.value })}
                    >
                      {_officeworksStoreList.map((store) => (
                        <option key={store.store_id} value={store.store_id}>
                          {store.store_name} ({store.state})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pg-section-label" style={{ marginTop: '8px' }}>Customer</div>

                  {/* Postcode */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="ow-postcode">
                      Customer postcode <span className="req">*</span>
                    </label>
                    <select
                      className="mca-select"
                      id="ow-postcode"
                      value={officeworksState.postcode}
                      onChange={(e) => setOfficeworksState({ ...officeworksState, postcode: e.target.value })}
                    >
                      <option value="">Select postcode...</option>
                      {officeworksZipCodes.map((zc) => (
                        <option key={zc.id} value={zc.id}>
                          {zc.zipcode}
                        </option>
                      ))}
                    </select>
                  </div>
                </form>
              )}

              {/* Repco form — postcode-driven, no store */}
              {activeApi === 'repco' && (
                <form className="form-stack">
                  <div className="pg-section-label">Product</div>

                  {/* Product code */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="rp-sku">
                      Product code <span className="req">*</span>
                    </label>
                    <input
                      className="mca-input mca-cell-mono"
                      id="rp-sku"
                      value={repcoState.sku}
                      onChange={(e) => setRepcoState({ ...repcoState, sku: e.target.value })}
                    />
                    <span className="mca-help">Repco product code (e.g. A1547402).</span>
                  </div>

                  <div className="pg-section-label" style={{ marginTop: '8px' }}>Customer</div>

                  {/* Postcode */}
                  <div className="mca-field">
                    <label className="mca-label" htmlFor="rp-postcode">
                      Customer postcode <span className="req">*</span>
                    </label>
                    <select
                      className="mca-select"
                      id="rp-postcode"
                      value={repcoState.postcode}
                      onChange={(e) => setRepcoState({ ...repcoState, postcode: e.target.value })}
                    >
                      <option value="">Select postcode...</option>
                      {repcoZipCodes.map((zc) => (
                        <option key={zc.id} value={zc.id}>
                          {zc.zipcode}
                        </option>
                      ))}
                    </select>
                  </div>
                </form>
              )}

              {/* Send button */}
              <button
                className="mca-btn mca-btn--primary"
                id="sendBtn"
                style={{ width: '100%', marginTop: '24px' }}
                onClick={handleSend}
                disabled={loading}
              >
                {loading && <span className="mca-spinner" />}
                {!loading && <SendIcon />}
                <span style={{ marginLeft: '8px' }}>
                  {loading ? 'Sending…' : 'Send API request'}
                </span>
              </button>
              {loading && (
                <p className="mca-help" style={{ textAlign: 'center', marginTop: '8px' }}>
                  The request will take a few seconds…
                </p>
              )}
            </div>
          </div>

          {/* RIGHT: Results / Code card */}
          <div className="mca-card pg-right">
            <div className="mca-card__head" style={{ paddingBottom: '0', borderBottom: 'none' }}>
              <div className="mca-tabs">
                <button
                  className="mca-tab"
                  role="tab"
                  aria-selected={activeResultPane === 'code'}
                  onClick={() => setActiveResultPane('code')}
                >
                  <CodeIcon />
                  Code sample
                </button>
                <button
                  className="mca-tab"
                  role="tab"
                  aria-selected={activeResultPane === 'results'}
                  onClick={() => setActiveResultPane('results')}
                >
                  <ResultsIcon />
                  Results
                </button>
              </div>
            </div>
            <div className="mca-card__body pg-pane">
              {activeResultPane === 'code' && (
                <CodeSampleLanguages api={activeApi} params={sampleParams} apiKey={apiKey} />
              )}
              {activeResultPane === 'results' && <ResultsPane data={_playgroundData} loading={loading} />}
            </div>
          </div>
        </div>
      </div>
    </Default>
  );
};
